import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import {
  type ChatRequestOptions,
  type CreateUIMessage,
  type FileUIPart,
} from 'ai';
import { useChat } from '@ai-sdk/react';
import type { ChatDocumentDTO, ChatDocumentDeltaDTO } from '@shared/schemas/chat';
import { CHAT_ERROR_CODES, QUOTA_ERROR_CODES } from '@shared/constants/error-codes';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { captureFrontendException } from '@/lib/sentry';
import { conversationKeys } from './useConversation';
import {
  patchConversationTitleInCache,
  seedConversationDetailCache,
  syncConversationDetailCache,
} from './chat-session-cache';
import type { AskUserToolOutputDTO } from '@shared/schemas/chat-ask-user';
import {
  buildAnsweredInTextOutput,
  findPendingAskUserPart,
  getMessageTextFromParts,
  isToolContinuationAlreadyAppliedError,
  lastAssistantMessageIsCompleteWithAskUserOutputs,
  messagePartsHaveFiles,
  shouldInterceptComposerForAskUser,
} from '../chat-ask-user-flow';
import { isChatComposerLocked } from '../chat-composer-state';
import {
  CHAT_CLIENT_ERROR_CODES,
  CHAT_CONCURRENT_TURN_LIMIT_MESSAGE,
  extractChatErrorCode,
  getChatErrorMessage,
} from '../chat-errors';
import { deriveAssistantVisualState } from '../assistant-visual-state';
import {
  createChatTurnErrorState,
  createDisconnectTurnErrorState,
  resolveChatStreamOutcome,
  shouldCaptureChatTurnError,
  type ChatStreamOutcome,
} from '../chat-turn-error';
import {
  countChatUxEvent,
  markChatUserMessageVisible,
  startChatUxTurn,
} from '../chat-ux-metrics';
import {
  buildConversationPath,
} from '../chat-session-intent';
import { getConversation, stopChatTurn } from '../services/chat.api';
import type { ChatUIMessage } from '../types';
import { toChatUiMessages } from '../types';
import { conversationChatRegistry } from '../chat-stream-registry';

const LOCAL_DOCUMENT_MESSAGE_PREFIX = 'local-document-';

type UseChatStreamOptions = {
  conversationId?: string;
  initialMessages: ChatUIMessage[];
  initialMessagesReady?: boolean;
  newChatKey?: string | null;
  ownerId?: string;
  quotaBlocked?: boolean;
  ragEnabled?: boolean;
  webSearchEnabled?: boolean;
  onChatTitle?: (payload: { conversationId: string; title: string | null }) => void;
  onDocumentDelta?: (payload: ChatDocumentDeltaDTO) => void;
  onDocumentFinish?: (document: ChatDocumentDTO) => void;
  onDocumentOpen?: (document: ChatDocumentDTO) => void;
  onQuotaExceeded?: () => void;
  onStreamSettled?: (outcome: ChatStreamOutcome) => void;
};

type ChatSendMessageInput =
  | (CreateUIMessage<ChatUIMessage> & {
    files?: never;
    messageId?: string;
    text?: never;
  })
  | {
    files?: FileList | FileUIPart[];
    messageId?: string;
    metadata?: ChatUIMessage['metadata'];
    parts?: never;
    text: string;
  }
  | {
    files: FileList | FileUIPart[];
    messageId?: string;
    metadata?: ChatUIMessage['metadata'];
    parts?: never;
  };

type ChatRegenerateOptions = ChatRequestOptions & {
  messageId?: string;
};

function getLocalDocumentMessageId(documentId: string) {
  return `${LOCAL_DOCUMENT_MESSAGE_PREFIX}${documentId}`;
}

function createLocalDocumentMessage(document: ChatDocumentDTO): ChatUIMessage {
  return {
    id: getLocalDocumentMessageId(document.documentId),
    parts: [{
      data: document,
      id: document.documentId,
      type: 'data-document-open',
    } as ChatUIMessage['parts'][number]],
    role: 'assistant',
  };
}

function upsertDocumentMessage(
  messages: ChatUIMessage[],
  document: ChatDocumentDTO,
): ChatUIMessage[] {
  let hasUpdatedDocument = false;

  const nextMessages = messages.map((message) => {
    let messageChanged = false;

    const nextParts = message.parts.map((part) => {
      if (
        (part.type === 'data-document-open' || part.type === 'data-document-preview' || part.type === 'data-document-finish')
        && part.data.documentId === document.documentId
      ) {
        hasUpdatedDocument = true;
        messageChanged = true;

        return {
          ...part,
          data: document,
          id: document.documentId,
          type: document.status === 'streaming' ? 'data-document-open' : 'data-document-finish',
        } as ChatUIMessage['parts'][number];
      }

      return part;
    });

    return messageChanged
      ? {
        ...message,
        parts: nextParts,
      }
      : message;
  });

  if (hasUpdatedDocument) {
    return nextMessages;
  }

  return [
    ...nextMessages,
    createLocalDocumentMessage(document),
  ];
}

function applyDocumentDelta(
  messages: ChatUIMessage[],
  payload: ChatDocumentDeltaDTO,
): ChatUIMessage[] {
  return messages.map((message) => {
    let messageChanged = false;

    const nextParts = message.parts.map((part) => {
      if (
        (part.type === 'data-document-open' || part.type === 'data-document-preview' || part.type === 'data-document-finish')
        && part.data.documentId === payload.documentId
      ) {
        messageChanged = true;
        const nextContent = `${part.data.content}${payload.delta}`;

        return {
          ...part,
          data: {
            ...part.data,
            content: nextContent,
            status: 'streaming',
            summary: nextContent.trim().slice(0, 220) || part.data.summary,
          },
          type: 'data-document-open',
        } as ChatUIMessage['parts'][number];
      }

      return part;
    });

    return messageChanged
      ? {
        ...message,
        parts: nextParts,
      }
      : message;
  });
}

function deriveTurnErrorContext(messages: ChatUIMessage[]) {
  const failedUserMessage = [...messages].reverse().find((message) => message.role === 'user');
  const lastAssistantMessage = [...messages].reverse().find((message) => message.role === 'assistant');

  return {
    failedUserMessageId: failedUserMessage?.id ?? null,
    hasPartialResponse: Boolean(
      lastAssistantMessage
      && ['activity', 'content'].includes(deriveAssistantVisualState({
        message: lastAssistantMessage,
        status: 'error',
      })),
    ),
  };
}

export function useChatStream(options: UseChatStreamOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const routedConversationIdRef = useRef<string | undefined>(options.conversationId);
  const entry = conversationChatRegistry.select({
    conversationId: options.conversationId,
    initialMessages: options.initialMessages,
    initialMessagesReady: options.initialMessagesReady ?? true,
    newChatKey: options.newChatKey,
    ownerId: options.ownerId,
  });
  const runtime = useSyncExternalStore(
    entry.subscribeRuntime,
    entry.getRuntimeSnapshot,
    entry.getRuntimeSnapshot,
  );
  const requestConversationId = entry.conversationId ?? options.conversationId;
  const chat = useChat<ChatUIMessage>({
    chat: entry.chat,
    // Lissage du stream : coalesce les chunks SSE (le H100 émet en rafales
    // irrégulières) en mises à jour UI à cadence bornée — sans ça, chaque
    // delta re-parse tout le markdown et le rendu saccade.
    experimental_throttle: 50,
  });

  const syncConversationRoute = useCallback((conversationId: string) => {
    if (!conversationChatRegistry.isDisplayed(entry)) {
      return;
    }

    if (routedConversationIdRef.current === conversationId) {
      return;
    }

    routedConversationIdRef.current = conversationId;
    const nextPath = buildConversationPath(
      conversationId,
      options.ragEnabled ? 'document-search' : 'general',
    );
    startTransition(() => {
      navigate(nextPath, { replace: true });
    });
  }, [entry, navigate, options.ragEnabled]);

  useEffect(() => {
    routedConversationIdRef.current = options.conversationId;
  }, [options.conversationId]);

  const resyncConversationAfterToolContinuationNoOp = useCallback(async (conversationId: string) => {
    const payload = await getConversation(conversationId);
    entry.replaceMessages(toChatUiMessages(payload));
    syncConversationDetailCache(queryClient, conversationId, toChatUiMessages(payload), payload.conversation);
  }, [entry, queryClient]);

  // L'instance Chat survit au composant : rafraîchir son porteur de handlers
  // après chaque rendu validé évite les closures du premier montage.
  useLayoutEffect(() => {
    entry.setHandlers({
      onData: async (dataPart) => {
        if (dataPart.type === 'data-conversation') {
          const nextConversationId = dataPart.data.conversationId;

          syncConversationRoute(nextConversationId);
          seedConversationDetailCache(queryClient, dataPart.data);

          void queryClient.invalidateQueries({ queryKey: conversationKeys.all });

          return;
        }

        if (dataPart.type === 'data-chat-title') {
          patchConversationTitleInCache(
            queryClient,
            dataPart.data.conversationId,
            dataPart.data.title,
          );

          void queryClient.invalidateQueries({ queryKey: conversationKeys.all });

          if (conversationChatRegistry.isDisplayed(entry)) {
            options.onChatTitle?.(dataPart.data);
          }

          return;
        }

        if (dataPart.type === 'data-document-open') {
          entry.replaceMessages((messages) => upsertDocumentMessage(messages, dataPart.data));
          if (conversationChatRegistry.isDisplayed(entry)) {
            options.onDocumentOpen?.(dataPart.data);
          }
          return;
        }

        if (dataPart.type === 'data-document-delta') {
          entry.replaceMessages((messages) => applyDocumentDelta(messages, dataPart.data));
          if (conversationChatRegistry.isDisplayed(entry)) {
            options.onDocumentDelta?.(dataPart.data);
          }
          return;
        }

        if (dataPart.type === 'data-document-finish') {
          entry.replaceMessages((messages) => upsertDocumentMessage(messages, dataPart.data));
          if (conversationChatRegistry.isDisplayed(entry)) {
            options.onDocumentFinish?.(dataPart.data);
          }
        }
      },
      onError: (error) => {
        const errorCode = extractChatErrorCode(error);

        if (isToolContinuationAlreadyAppliedError(errorCode)) {
          entry.chat.clearError();
          entry.setLastAskUserSubmission(null);
          entry.updateRuntime({
            isAskUserSubmitting: false,
            toolContinuationError: null,
          });

          const activeConversationId = entry.conversationId;
          if (activeConversationId) {
            void resyncConversationAfterToolContinuationNoOp(activeConversationId);
            syncConversationRoute(activeConversationId);
          }

          return;
        }

        const lastAskUserSubmission = entry.getLastAskUserSubmission();
        if (lastAskUserSubmission) {
          entry.updateRuntime({
            isAskUserSubmitting: false,
            toolContinuationError: lastAskUserSubmission,
          });
        }

        const nextTurnError = createChatTurnErrorState({
          error,
          ...deriveTurnErrorContext(entry.chat.messages),
        });
        entry.updateRuntime({ turnError: nextTurnError });

        if (
          errorCode === CHAT_ERROR_CODES.QUOTA_EXCEEDED
          || errorCode === QUOTA_ERROR_CODES.COMPANY_QUOTA_EXCEEDED
        ) {
          options.onQuotaExceeded?.();
        }

        if (shouldCaptureChatTurnError(nextTurnError)) {
          captureFrontendException(error, {
            extra: {
              conversationPresent: Boolean(entry.conversationId),
            },
            tags: {
              'chat.error_kind': nextTurnError.kind,
              'chat.phase': 'stream',
              'chat.rag_enabled': options.ragEnabled ? 'true' : 'false',
              'chat.web_search_enabled': options.webSearchEnabled ? 'true' : 'false',
            },
          });
        }
        if (conversationChatRegistry.isDisplayed(entry)) {
          toast.error(nextTurnError.message);
        }

        if (entry.conversationId) {
          syncConversationRoute(entry.conversationId);
        }
      },
      onFinish: ({ finishReason, isAbort, isDisconnect, isError, messages }) => {
        const outcome = resolveChatStreamOutcome({
          finishReason,
          isAbort,
          isDisconnect,
          isError,
        });

        entry.updateRuntime({
          isAskUserSubmitting: false,
          isStopRequested: false,
        });
        const activeConversationId = entry.conversationId;

        if (outcome.type === 'success') {
          entry.setLastAskUserSubmission(null);
          entry.updateRuntime({
            toolContinuationError: null,
            turnError: null,
          });
        }

        if (outcome.type === 'success' && activeConversationId) {
          syncConversationDetailCache(queryClient, activeConversationId, messages);
          syncConversationRoute(activeConversationId);
        }

        if (outcome.type === 'success') {
          void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
        }

        // Coupure SSE en vol non remontée par `onError` : matérialiser une erreur de
        // tour réseau réessayable plutôt que de laisser une réponse tronquée muette.
        if (outcome.type === 'disconnected') {
          const disconnectTurnError = createDisconnectTurnErrorState(
            deriveTurnErrorContext(entry.chat.messages.length > 0 ? entry.chat.messages : messages),
          );
          entry.updateRuntime({ turnError: disconnectTurnError });
          if (conversationChatRegistry.isDisplayed(entry)) {
            toast.error(disconnectTurnError.message);
          }

          if (entry.conversationId) {
            syncConversationRoute(entry.conversationId);
          }
        }

        options.onStreamSettled?.(outcome);
      },
      sendAutomaticallyWhen: ({ messages }) => {
        if (!lastAssistantMessageIsCompleteWithAskUserOutputs({ messages })) {
          return false;
        }

        if (conversationChatRegistry.tryAcquireTurn(entry)) {
          return true;
        }

        entry.updateRuntime({
          isAskUserSubmitting: false,
          toolContinuationError: entry.getLastAskUserSubmission(),
        });
        toast.error(CHAT_CONCURRENT_TURN_LIMIT_MESSAGE);
        return false;
      },
    });
  });

  useLayoutEffect(() => {
    conversationChatRegistry.markDisplayed(entry);

    return () => {
      conversationChatRegistry.releaseDisplayed(entry);
    };
  }, [entry]);

  const withChatRequestBody = useCallback((requestOptions?: ChatRequestOptions): ChatRequestOptions => ({
    ...requestOptions,
    body: {
      ...requestOptions?.body,
      conversationId: requestConversationId,
      ragEnabled: options.ragEnabled,
      turnId: entry.getRequestTurnId() ?? undefined,
      webSearchEnabled: options.webSearchEnabled,
    },
  }), [entry, options.ragEnabled, options.webSearchEnabled, requestConversationId]);

  const submitAskUserOutput = useCallback(({
    output,
    toolCallId,
  }: {
    output: AskUserToolOutputDTO;
    toolCallId: string;
  }) => {
    entry.updateRuntime({
      isAskUserSubmitting: true,
      toolContinuationError: null,
    });
    entry.setLastAskUserSubmission({ output, toolCallId });
    conversationChatRegistry.prepareTurn(entry);

    void Promise.resolve(chat.addToolOutput({
      options: withChatRequestBody(),
      output,
      tool: 'ask_user',
      toolCallId,
    })).catch((error: unknown) => {
      conversationChatRegistry.releaseTurn(entry);
      entry.updateRuntime({
        isAskUserSubmitting: false,
        toolContinuationError: entry.getLastAskUserSubmission(),
      });
      toast.error(getChatErrorMessage(error));
    });
  }, [chat, entry, withChatRequestBody]);

  const retryAskUserContinuation = useCallback(() => {
    const pendingRetry = runtime.toolContinuationError ?? entry.getLastAskUserSubmission();

    if (!pendingRetry) {
      return;
    }

    chat.clearError();
    countChatUxEvent('chat.ui.ask_user_retry');
    submitAskUserOutput(pendingRetry);
  }, [chat, entry, runtime.toolContinuationError, submitAskUserOutput]);

  const reset = useCallback(() => {
    conversationChatRegistry.resetEntry(entry);
  }, [entry]);

  const sendMessage = useCallback((message?: ChatSendMessageInput, requestOptions?: ChatRequestOptions) => {
    if (message && 'parts' in message && Array.isArray(message.parts)) {
      const trimmedInput = getMessageTextFromParts(message.parts);
      const hasReadyAttachments = messagePartsHaveFiles(message.parts);
      const pendingAskUser = findPendingAskUserPart(chat.messages);
      const composerAction = shouldInterceptComposerForAskUser({
        hasReadyAttachments,
        pendingAskUser: pendingAskUser !== null,
        trimmedInput,
      });

      if (composerAction === 'reject-attachments-only') {
        throw { code: 'ASK_USER_PENDING_ATTACHMENTS' };
      }

      if (composerAction === 'intercept-text' && pendingAskUser) {
        submitAskUserOutput({
          output: buildAnsweredInTextOutput(trimmedInput),
          toolCallId: pendingAskUser.toolCallId,
        });
        return Promise.resolve();
      }
    }

    if (isChatComposerLocked({
      isAskUserSubmitting: runtime.isAskUserSubmitting,
      quotaBlocked: options.quotaBlocked,
      status: chat.status,
    })) {
      return Promise.resolve();
    }

    if (!conversationChatRegistry.tryAcquireTurn(entry)) {
      return Promise.reject({
        code: CHAT_CLIENT_ERROR_CODES.CONCURRENT_TURN_LIMIT,
      });
    }

    chat.clearError();
    entry.updateRuntime({ turnError: null });
    startChatUxTurn();

    const request = chat
      .sendMessage(message, withChatRequestBody(requestOptions))
      .catch((error: unknown) => {
        conversationChatRegistry.releaseTurn(entry);
        throw error;
      });
    markChatUserMessageVisible();
    return request;
  }, [
    chat,
    entry,
    options.quotaBlocked,
    runtime.isAskUserSubmitting,
    submitAskUserOutput,
    withChatRequestBody,
  ]);

  const clearTurnError = useCallback(() => {
    chat.clearError();
    entry.updateRuntime({ turnError: null });
  }, [chat, entry]);

  const retryTurn = useCallback(() => {
    if (!runtime.turnError?.retryable) {
      return Promise.resolve();
    }

    if (!conversationChatRegistry.tryAcquireTurn(entry)) {
      toast.error(CHAT_CONCURRENT_TURN_LIMIT_MESSAGE);
      return Promise.resolve();
    }

    const messageId = runtime.turnError.failedUserMessageId ?? undefined;
    chat.clearError();
    entry.updateRuntime({ turnError: null });
    countChatUxEvent('chat.ui.retry_after_stream_error');
    startChatUxTurn();

    const requestOptions = withChatRequestBody();

    return chat
      .regenerate({
        ...requestOptions,
        messageId,
      })
      .catch((error: unknown) => {
        conversationChatRegistry.releaseTurn(entry);
        throw error;
      });
  }, [
    chat,
    entry,
    runtime.turnError,
    withChatRequestBody,
  ]);

  const stop = useCallback(() => {
    if (
      entry.getRuntimeSnapshot().isStopRequested
      || (chat.status !== 'submitted' && chat.status !== 'streaming')
    ) {
      return;
    }

    entry.updateRuntime({ isStopRequested: true });
    countChatUxEvent('chat.ui.user_abort');
    const turnId = entry.getActiveTurnId();

    if (!turnId) {
      entry.updateRuntime({ isStopRequested: false });
      toast.error('Impossible d’identifier la réponse à arrêter.');
      return;
    }

    void stopChatTurn(turnId)
      .then(() => chat.stop())
      .catch((error: unknown) => {
        if (extractChatErrorCode(error) === CHAT_ERROR_CODES.CHAT_TURN_NOT_FOUND) {
          return chat.stop();
        }

        entry.updateRuntime({ isStopRequested: false });
        toast.error('L’arrêt de la réponse n’a pas pu être confirmé. Réessayez.');
        captureFrontendException(error, {
          tags: {
            'chat.phase': 'stop',
          },
        });
      });
  }, [chat, entry]);

  const regenerate = useCallback((requestOptions?: ChatRegenerateOptions) => {
    if (options.quotaBlocked) {
      return Promise.resolve();
    }

    if (!conversationChatRegistry.tryAcquireTurn(entry)) {
      toast.error(CHAT_CONCURRENT_TURN_LIMIT_MESSAGE);
      return Promise.resolve();
    }

    chat.clearError();
    entry.updateRuntime({ turnError: null });
    startChatUxTurn();
    return chat
      .regenerate(withChatRequestBody(requestOptions))
      .catch((error: unknown) => {
        conversationChatRegistry.releaseTurn(entry);
        throw error;
      });
  }, [
    chat,
    entry,
    options.quotaBlocked,
    withChatRequestBody,
  ]);

  return useMemo(() => ({
    ...chat,
    clearTurnError,
    hasPendingAskUser: findPendingAskUserPart(chat.messages) !== null,
    isAskUserSubmitting: runtime.isAskUserSubmitting,
    isStopRequested: runtime.isStopRequested,
    regenerate,
    reset,
    retryAskUserContinuation,
    retryTurn,
    sendMessage,
    stop,
    submitAskUserOutput,
    toolContinuationError: runtime.toolContinuationError,
    turnError: runtime.turnError,
  }), [
    chat,
    clearTurnError,
    regenerate,
    reset,
    retryAskUserContinuation,
    retryTurn,
    runtime.isAskUserSubmitting,
    runtime.isStopRequested,
    runtime.toolContinuationError,
    runtime.turnError,
    sendMessage,
    stop,
    submitAskUserOutput,
  ]);
}
